import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Building2, Inbox, LogOut, Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PropertyForm, { EMPTY_PROPERTY, toForm, toPayload } from "./PropertyForm";

const STATUS_COLORS = {
  Available: "bg-green-100 text-green-800",
  Reserved: "bg-amber-100 text-amber-800",
  Sold: "bg-gray-200 text-gray-700",
  New: "bg-gold/20 text-gold-dark",
  Contacted: "bg-blue-100 text-blue-800",
  Closed: "bg-gray-200 text-gray-600",
};

function PropertiesAdmin() {
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/properties?all=true").then((r) => setProperties(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = toPayload(editing.form);
      if (editing.id) await api.put(`/properties/${editing.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing.id ? "Property updated" : "Property added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Property deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return (
      <div>
        <h2 className="mb-5 font-display text-2xl font-semibold text-navy">
          {editing.id ? "Edit Property" : "Add New Property"}
        </h2>
        <PropertyForm
          form={editing.form}
          setForm={(updater) => setEditing((e) => ({ ...e, form: updater(e.form) }))}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold text-navy">Properties ({properties.length})</h2>
        <button
          data-testid="add-property-button"
          onClick={() => setEditing({ id: null, form: EMPTY_PROPERTY })}
          className="flex items-center gap-2 rounded-sm bg-gold px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-gold-light"
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Add Property
        </button>
      </div>
      <div className="space-y-3">
        {properties.map((p) => (
          <div
            key={p.id}
            data-testid="admin-property-row"
            className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-center"
          >
            <img src={p.images?.[0]} alt="" className="h-16 w-24 shrink-0 rounded-sm object-cover" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-navy">{p.title}</p>
              <p className="truncate text-xs text-muted-foreground">
                {p.location} &bull; {p.property_type} &bull; {p.price_label}
                {p.featured ? " \u2022 Featured" : ""}
              </p>
            </div>
            <span className={`w-fit rounded-sm px-2.5 py-1 text-[11px] font-bold uppercase ${STATUS_COLORS[p.status] || ""}`}>
              {p.status}
            </span>
            <div className="flex gap-2">
              <button
                data-testid="edit-property-button"
                onClick={() => setEditing({ id: p.id, form: toForm(p) })}
                className="flex items-center gap-1.5 rounded-sm border border-navy/20 px-3.5 py-2 text-xs font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
              </button>
              <button
                data-testid="delete-property-button"
                onClick={() => remove(p)}
                className="flex items-center gap-1.5 rounded-sm border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-600 hover:text-white"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
              </button>
            </div>
          </div>
        ))}
        {properties.length === 0 && (
          <p className="rounded-xl border border-dashed border-navy/20 bg-white p-10 text-center text-sm text-muted-foreground">
            No properties yet. Add your first listing.
          </p>
        )}
      </div>
    </div>
  );
}

function EnquiriesAdmin() {
  const [enquiries, setEnquiries] = useState([]);

  const load = useCallback(() => {
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <h2 className="mb-5 font-display text-2xl font-semibold text-navy">Enquiries ({enquiries.length})</h2>
      <div className="space-y-3">
        {enquiries.map((e) => (
          <div key={e.id} data-testid="admin-enquiry-row" className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-navy">{e.name}</p>
                <p className="text-xs text-muted-foreground">
                  {e.mobile}
                  {e.email ? ` \u2022 ${e.email}` : ""}
                </p>
              </div>
              <select
                data-testid="enquiry-status-select"
                value={e.status}
                onChange={(ev) => setStatus(e.id, ev.target.value)}
                className={`rounded-sm px-2.5 py-1.5 text-xs font-bold uppercase ${STATUS_COLORS[e.status] || ""}`}
              >
                {["New", "Contacted", "Closed"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wider text-navy/60">
              {e.property_type && <span className="rounded-sm bg-navy-50 px-2 py-1">{e.property_type}</span>}
              {e.preferred_location && <span className="rounded-sm bg-navy-50 px-2 py-1">{e.preferred_location}</span>}
              {e.budget && <span className="rounded-sm bg-navy-50 px-2 py-1">{e.budget}</span>}
              {e.plot_size && <span className="rounded-sm bg-navy-50 px-2 py-1">{e.plot_size}</span>}
              {e.property_title && <span className="rounded-sm bg-gold/15 px-2 py-1 text-gold-dark">{e.property_title}</span>}
            </div>
            {e.message && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{e.message}</p>}
            <p className="mt-3 text-[11px] text-muted-foreground">
              {new Date(e.created_at).toLocaleString("en-IN")}
            </p>
          </div>
        ))}
        {enquiries.length === 0 && (
          <p className="rounded-xl border border-dashed border-navy/20 bg-white p-10 text-center text-sm text-muted-foreground">
            No enquiries yet. New form submissions will appear here.
          </p>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("properties");

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Loading...</p>
      </main>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-gold text-navy">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <p className="font-display text-lg font-bold leading-tight text-navy">Admin Panel</p>
              <p className="text-[11px] text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 rounded-sm border border-navy/20 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8 flex gap-2">
          {[
            { id: "properties", label: "Properties", icon: Building2 },
            { id: "enquiries", label: "Enquiries", icon: Inbox },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy hover:border-navy"
              }`}
            >
              <t.icon className="h-4 w-4" aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        {tab === "properties" ? <PropertiesAdmin /> : <EnquiriesAdmin />}
      </div>
    </main>
  );
}
